<template>
  <main class="app-content">
    <div class="app-title">
      <div>
        <h1><i class="fa fa-database"></i> Cold Lake <small class="text-muted">(data cũ — phonenet-data)</small></h1>
        <p class="mb-0 text-muted">Theo dõi sức khoẻ kho dữ liệu cũ tự host: disk, file, ingest, backup, cảnh báo.</p>
      </div>
      <ul class="app-breadcrumb breadcrumb">
        <li class="breadcrumb-item"><i class="fa fa-home fa-lg"></i></li>
        <li class="breadcrumb-item">Công cụ</li>
        <li class="breadcrumb-item">Cold Lake</li>
      </ul>
    </div>

    <div class="d-flex justify-content-end mb-2">
      <b-button size="sm" variant="outline-secondary" :disabled="loading" @click="loadAll">
        <b-spinner small v-if="loading" /> <i class="fa fa-sync" v-else></i> Làm mới
      </b-button>
    </div>

    <!-- Tổng quan -->
    <div class="row">
      <div class="col-md-3 col-6 mb-3">
        <div class="card"><div class="card-body py-3">
          <div class="text-muted small">Số node</div>
          <div class="h4 mb-0">{{ summary.nodeCount || nodes.length }}</div>
        </div></div>
      </div>
      <div class="col-md-3 col-6 mb-3">
        <div class="card" :class="{ 'border-danger': summary.activeAlerts24h > 0 }"><div class="card-body py-3">
          <div class="text-muted small">Cảnh báo 24h</div>
          <div class="h4 mb-0" :class="summary.activeAlerts24h > 0 ? 'text-danger' : ''">{{ summary.activeAlerts24h || 0 }}</div>
        </div></div>
      </div>
      <div class="col-md-6 mb-3">
        <div class="card"><div class="card-body py-2">
          <div class="text-muted small mb-1">Ingest gần nhất</div>
          <div v-for="(run, cmd) in summary.lastIngest" :key="cmd" class="small d-flex justify-content-between">
            <span>{{ cmd }}</span>
            <span v-if="run">
              <b-badge :variant="run.status === 'done' ? 'success' : 'danger'">{{ run.status }}</b-badge>
              {{ run.recordCount }} bản · {{ fmtAgo(run.startTime) }}
            </span>
            <span v-else class="text-muted">chưa chạy</span>
          </div>
        </div></div>
      </div>
    </div>

    <!-- Đọc data cũ: DATA gánh được bao nhiêu cho LAKE (7 ngày) -->
    <div class="card mb-3">
      <div class="card-header py-2">
        <i class="fa fa-exchange-alt"></i> Đọc data cũ — DATA gánh cho LAKE
        <small class="text-muted">(7 ngày · tab Lưu trữ)</small>
      </div>
      <div class="card-body py-3">
        <div class="row text-center">
          <div class="col-md-3 col-6">
            <div class="text-muted small">Đọc từ DATA (disk)</div>
            <div class="h4 mb-0 text-success">{{ routed.data || 0 | numFormat }}</div>
            <div class="small text-muted">miễn phí</div>
          </div>
          <div class="col-md-3 col-6">
            <div class="text-muted small">Quét LAKE (S3)</div>
            <div class="h4 mb-0" :class="routed.lakeScan ? 'text-warning' : ''">{{ routed.lakeScan || 0 | numFormat }}</div>
            <div class="small text-muted">${{ (routed.lakeCostUsd || 0).toFixed(4) }}</div>
          </div>
          <div class="col-md-3 col-6">
            <div class="text-muted small">Trang đọc lại (Athena)</div>
            <div class="h4 mb-0">{{ routed.lakeResume || 0 | numFormat }}</div>
            <div class="small text-muted">không quét, $0</div>
          </div>
          <div class="col-md-3 col-6">
            <div class="text-muted small">Tỉ lệ né được lake</div>
            <div class="h4 mb-0" :class="routed.dataSharePct >= 50 ? 'text-success' : 'text-muted'">
              {{ routed.dataSharePct || 0 }}%
            </div>
            <div class="small text-muted">DATA ÷ (DATA + LAKE)</div>
          </div>
        </div>
        <div v-if="!routed.data && !routed.lakeScan" class="alert alert-secondary mt-3 mb-0 py-2 small">
          Chưa có lượt đọc nào. Nếu chưa bật <code>phonenet_data_enabled</code> ở
          <b-link to="/etc">Tham số</b-link> thì mọi lượt đọc vẫn đi thẳng vào lake như cũ.
        </div>
      </div>
    </div>

    <div v-if="!loading && !nodes.length" class="alert alert-info">
      Chưa có snapshot nào. Đảm bảo <code>bin/monitor</code> đã chạy (cron) trên node phonenet-data.
    </div>

    <!-- Mỗi node 1 card -->
    <div v-for="n in nodes" :key="n.node" class="card mb-3">
      <div class="card-header d-flex justify-content-between align-items-center py-2">
        <span><i class="fa fa-server"></i> <strong>{{ n.node }}</strong>
          <b-badge v-if="n.staleMs > 20 * 60000" variant="warning" class="ml-2">snapshot cũ {{ fmtDur(n.staleMs) }}</b-badge>
        </span>
        <small class="text-muted">cập nhật {{ fmtAgo(n.createTime) }} · uptime {{ fmtDur(n.uptimeMs) }}</small>
      </div>
      <div class="card-body">
        <div class="row">
          <!-- Disk -->
          <div class="col-md-4 mb-3">
            <div class="text-muted small mb-1">Disk DATA_DIR</div>
            <div v-if="n.disk">
              <div class="progress" style="height: 20px;">
                <div class="progress-bar" :class="diskClass(n.disk.usedPct)" :style="{ width: n.disk.usedPct + '%' }">
                  {{ n.disk.usedPct }}%
                </div>
              </div>
              <small class="text-muted">{{ fmtBytes(n.disk.usedBytes) }} / {{ fmtBytes(n.disk.totalBytes) }} — trống {{ fmtBytes(n.disk.freeBytes) }}</small>
            </div>
            <div v-else class="text-muted small">—</div>

            <div class="text-muted small mt-3 mb-1">Backup gần nhất</div>
            <div v-if="n.lastBackup" class="small">
              {{ fmtAgo(n.lastBackup.createTime) }}
              <span v-if="n.lastBackup.data"> · {{ fmtBytes(n.lastBackup.data.sizeBytes) }}</span>
              <b-badge v-if="Date.now() - n.lastBackup.createTime > 26 * 3600000" variant="danger" class="ml-1">quá hạn</b-badge>
            </div>
            <div v-else class="text-danger small">Chưa có backup</div>
          </div>

          <!-- Datasets -->
          <div class="col-md-8">
            <div class="text-muted small mb-1">File theo dataset</div>
            <table class="table table-sm table-bordered mb-0">
              <thead><tr class="small text-muted">
                <th>Dataset</th><th class="text-right">File</th><th class="text-right">Dung lượng</th>
                <th class="text-right">TB/file</th><th class="text-right">File nhỏ</th><th class="text-right">Partition</th><th class="text-right">.tmp</th>
              </tr></thead>
              <tbody>
                <tr v-for="(s, name) in n.datasets" :key="name" class="small">
                  <td>{{ name }}</td>
                  <td class="text-right">{{ s.files }}</td>
                  <td class="text-right">{{ fmtBytes(s.totalBytes) }}</td>
                  <td class="text-right">{{ s.avgFileMB }}MB</td>
                  <td class="text-right" :class="s.compactablePartitions > 0 ? 'text-warning font-weight-bold' : ''">
                    {{ Math.round((s.smallFileRatio || 0) * 100) }}%<small v-if="s.compactablePartitions > 0" class="text-muted"> ({{ s.compactablePartitions }} gộp)</small>
                  </td>
                  <td class="text-right">{{ s.partitions }}</td>
                  <td class="text-right" :class="s.orphanTmp > 0 ? 'text-danger' : ''">{{ s.orphanTmp }}</td>
                </tr>
                <tr v-if="!n.datasets || !Object.keys(n.datasets).length"><td colspan="7" class="text-muted small">—</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Cảnh báo gần đây -->
    <div class="card mb-3">
      <div class="card-header py-2"><i class="fa fa-bell"></i> Cảnh báo gần đây (7 ngày)</div>
      <div class="card-body p-0">
        <table class="table table-sm mb-0">
          <tbody>
            <tr v-for="a in alerts" :key="a._id" class="small">
              <td style="width: 110px;">{{ fmtAgo(a.createTime) }}</td>
              <td style="width: 90px;">
                <b-badge :variant="a.alert && a.alert.severity === 'critical' ? 'danger' : 'warning'">
                  {{ a.alert ? a.alert.severity : '' }}
                </b-badge>
              </td>
              <td>{{ a.alert ? a.alert.message : '' }}</td>
            </tr>
            <tr v-if="!alerts.length"><td class="text-muted small">Không có cảnh báo — 🎉</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Lịch sử ingest -->
    <div class="card mb-3">
      <div class="card-header py-2"><i class="fa fa-history"></i> Lịch sử ingest</div>
      <div class="card-body p-0">
        <table class="table table-sm mb-0">
          <thead><tr class="small text-muted">
            <th>Lệnh</th><th>Node</th><th>Bởi</th><th class="text-right">Bản ghi</th><th class="text-right">File</th>
            <th>Trạng thái</th><th class="text-right">Thời lượng</th><th>Lúc</th>
          </tr></thead>
          <tbody>
            <tr v-for="r in runs" :key="r._id" class="small">
              <td>{{ r.command }}</td><td>{{ r.node }}</td><td>{{ r.triggeredBy }}</td>
              <td class="text-right">{{ r.recordCount }}</td><td class="text-right">{{ r.batchCount }}</td>
              <td><b-badge :variant="r.status === 'done' ? 'success' : r.status === 'failed' ? 'danger' : 'secondary'">{{ r.status }}</b-badge></td>
              <td class="text-right">{{ r.durationMs ? fmtDur(r.durationMs) : '—' }}</td>
              <td>{{ fmtAgo(r.startTime) }}</td>
            </tr>
            <tr v-if="!runs.length"><td colspan="8" class="text-muted small">Chưa có lần ingest nào</td></tr>
          </tbody>
        </table>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'ColdLake',
  data() {
    return { loading: false, summary: {}, nodes: [], alerts: [], runs: [], routed: {} };
  },
  mounted() { this.loadAll(); },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        const [summary, nodes, alerts, runs, lake] = await Promise.all([
          this.$service.get('/cold-lake/summary'),
          this.$service.get('/cold-lake/nodes'),
          this.$service.get('/cold-lake/alerts', { params: { range: '7d', limit: 100 } }),
          this.$service.get('/cold-lake/runs', { params: { limit: 50 } }),
          // nguồn thật của tab Lưu trữ: data-* (disk) vs lake-* (quét S3)
          this.$service.get('/s3-cost/lake-summary', { params: { range: '7d' } }),
        ]);
        this.summary = summary.data || {};
        this.nodes = (nodes.data && nodes.data.nodes) || [];
        this.alerts = (alerts.data && alerts.data.alerts) || [];
        this.runs = (runs.data && runs.data.runs) || [];
        this.routed = (lake.data && lake.data.routed) || {};
      } catch (e) {
        this.$bvToast && this.$bvToast.toast('Không tải được dữ liệu Cold Lake', { variant: 'danger', title: 'Lỗi' });
      } finally {
        this.loading = false;
      }
    },
    diskClass(pct) { return pct >= 90 ? 'bg-danger' : pct >= 80 ? 'bg-warning' : 'bg-success'; },
    fmtBytes(b) {
      if (b == null) return '—';
      const u = ['B', 'KB', 'MB', 'GB', 'TB']; let i = 0; b = Number(b);
      while (b >= 1024 && i < u.length - 1) { b /= 1024; i++; }
      return b.toFixed(i ? 1 : 0) + u[i];
    },
    fmtDur(ms) {
      ms = Number(ms) || 0; const s = Math.round(ms / 1000);
      if (s < 60) return s + 's';
      const m = Math.round(s / 60); if (m < 60) return m + 'p';
      const h = Math.floor(m / 60); if (h < 24) return h + 'h' + (m % 60 ? (m % 60) + 'p' : '');
      return Math.floor(h / 24) + 'd';
    },
    fmtAgo(t) {
      if (!t) return '—';
      const d = Date.now() - Number(t);
      if (d < 60000) return 'vừa xong';
      return this.fmtDur(d) + ' trước';
    },
  },
};
</script>
